import { getFirebaseFirestore } from './client'
import { traceLearningPerformance } from '@shared/learning/learning-performance-trace'

export interface FlashcardCategory {
  readonly featuredImage?: string
  readonly id: string
  readonly sort?: number
  readonly status?: string
  readonly title?: string
}

export interface FlashcardTranslationEntry {
  readonly code?: string
  readonly text?: string
}

export interface FlashcardCard {
  readonly categories?: string[]
  readonly femaleAudio?: string
  readonly featuredImage?: string
  readonly id: string
  readonly maleAudio?: string
  readonly sort?: number
  readonly status?: string
  readonly title?: string
  readonly translates?: FlashcardTranslationEntry[]
}

interface FlashcardDocumentSnapshot {
  readonly id: string
  readonly data: () => unknown
}

function requireFlashcardFirestore() {
  const firestore = getFirebaseFirestore()

  if (!firestore) {
    throw new Error('Cloud Firestore is not configured for this environment.')
  }

  return firestore
}

function sortByNumericOrder<T extends { readonly sort?: number }>(items: T[]) {
  return [...items].sort((left, right) => {
    const leftSort = typeof left.sort === 'number' ? left.sort : 0
    const rightSort = typeof right.sort === 'number' ? right.sort : 0

    return leftSort - rightSort
  })
}

function normalizeFlashcardDisplayText(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined

  return removeHiddenFlashcardText(value.normalize('NFC')).trim()
}

function removeHiddenFlashcardText(value: string): string {
  return [...value].filter((character) => !isHiddenFlashcardTextCharacter(character)).join('')
}

function isHiddenFlashcardTextCharacter(character: string): boolean {
  const codePoint = character.codePointAt(0) ?? 0

  return (
    codePoint <= 0x001f ||
    (codePoint >= 0x007f && codePoint <= 0x009f) ||
    (codePoint >= 0x200b && codePoint <= 0x200f) ||
    (codePoint >= 0x202a && codePoint <= 0x202e) ||
    (codePoint >= 0x2060 && codePoint <= 0x206f) ||
    codePoint === 0xfeff
  )
}

function normalizeFlashcardTranslations(
  value: unknown,
): FlashcardTranslationEntry[] | undefined {
  if (!Array.isArray(value)) return undefined

  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') return []

    const entry = item as FlashcardTranslationEntry
    const code = normalizeFlashcardDisplayText(entry.code)
    const text = normalizeFlashcardDisplayText(entry.text)

    if (!code && !text) return []

    return [{
      ...(code ? { code } : {}),
      ...(text ? { text } : {}),
    }]
  })
}

function normalizeFlashcardCategoryDocument(
  document: FlashcardDocumentSnapshot,
): FlashcardCategory {
  const data = document.data() as Omit<FlashcardCategory, 'id'>
  const title = normalizeFlashcardDisplayText(data.title)

  return {
    ...data,
    ...(title !== undefined ? { title } : {}),
    id: document.id,
  }
}

function normalizeFlashcardDocument(
  document: FlashcardDocumentSnapshot,
): FlashcardCard {
  const data = document.data() as Omit<FlashcardCard, 'id'>
  const title = normalizeFlashcardDisplayText(data.title)
  const translations = normalizeFlashcardTranslations(data.translates)

  return {
    ...data,
    ...(title !== undefined ? { title } : {}),
    ...(translations !== undefined ? { translates: translations } : {}),
    id: document.id,
  }
}

export async function getPublishedFlashcardCategories(): Promise<
  FlashcardCategory[]
> {
  const firestore = requireFlashcardFirestore()
  const snapshot = await traceLearningPerformance({
    event: 'flashcards.categories.query',
    getDetails: (querySnapshot) => ({ docs: querySnapshot.size }),
    task: () =>
      firestore
        .collection('flashcard_categories')
        .where('status', '==', 'published')
        .orderBy('sort', 'asc')
        .get(),
  })

  return sortByNumericOrder(
    snapshot.docs.map(normalizeFlashcardCategoryDocument),
  )
}

export async function getPublishedFlashcards(
  categoryId: string,
): Promise<FlashcardCard[]> {
  const firestore = requireFlashcardFirestore()
  const snapshot = await traceLearningPerformance({
    event: 'flashcards.cards.query',
    getDetails: (querySnapshot) => ({
      categoryId,
      docs: querySnapshot.size,
    }),
    task: () =>
      firestore
        .collection('flashcards')
        .where('status', '==', 'published')
        .where('categories', 'array-contains', categoryId)
        .orderBy('title', 'asc')
        .get(),
  })

  return snapshot.docs.map(normalizeFlashcardDocument)
}
